import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";
import { PASTEL_COLORS } from "@/lib/palette";

export const ANATOMY_COLORS = PASTEL_COLORS.slice(0, 6);

const IMAGES = {
  front: "https://static.prod-images.emergentagent.com/jobs/361c1c9e-1f5a-4cd0-86d1-9704ac80b69a/images/d2a50efb975139a784f6fff714cc3f22334fc5d3ff4a1ff0176c65c250a69ddc.jpeg",
  back: "https://static.prod-images.emergentagent.com/jobs/361c1c9e-1f5a-4cd0-86d1-9704ac80b69a/images/f31b2c4ba02794d24fcd2a5c24a4a3243a83eb034bde363cfe5da198d270c861.jpeg",
  digestive: "https://static.prod-images.emergentagent.com/jobs/361c1c9e-1f5a-4cd0-86d1-9704ac80b69a/images/ea1b5b5038d70aa130aaee45bdb8302b51a988dce4532224489d6d215f5f1094.jpeg",
};
const EMPTY = { front: [], back: [], digestive: [] };
const short = (t) => (t || "").trim().split(/\s+/).slice(0, 2).join(" ") || "Note";
const uid = () => Math.random().toString(36).slice(2, 9);

const AnatomyTab = forwardRef(function AnatomyTab({ soinId, initial, onSaved }, ref) {
  const [data, setData] = useState({ ...EMPTY, ...(initial || {}) });
  const [colors, setColors] = useState(ANATOMY_COLORS);
  const [open, setOpen] = useState(null); // note id being edited

  useEffect(() => {
    api.get("/settings").then((r) => { if (r.data?.anatomie_colors?.length) setColors(r.data.anatomie_colors); }).catch(() => {});
  }, []);

  const upd = (region, next) => setData((d) => ({ ...d, [region]: next }));
  const dropOpenEmpty = (d) => {
    const nd = {};
    for (const [r, arr] of Object.entries(d)) nd[r] = (arr || []).filter((n) => !(n.id === open && !(n.note || "").trim()));
    return nd;
  };
  const addNote = (region, x, y) => {
    const id = uid();
    setData((d) => { const nd = dropOpenEmpty(d); nd[region] = [...(nd[region] || []), { id, x, y, note: "", color: colors[0] }]; return nd; });
    setOpen(id);
  };
  const selectOpen = (id) => { if (open && open !== id) setData((d) => dropOpenEmpty(d)); setOpen(id); };
  const editNote = (region, id, patch) => upd(region, data[region].map((n) => n.id === id ? { ...n, ...patch } : n));
  const delNote = (region, id) => { upd(region, data[region].filter((n) => n.id !== id)); setOpen(null); };
  const closeNote = (region, id) => {
    const n = (data[region] || []).find((x) => x.id === id);
    if (n && !(n.note || "").trim()) delNote(region, id); else setOpen(null);
  };

  const save = async () => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, (v || []).filter((n) => (n.note || "").trim())]));
    try { await api.put(`/soins/${soinId}`, { anatomie: clean }); setData((d) => ({ ...d, ...clean })); setOpen(null); onSaved?.(clean); toast.success("Annotations anatomiques enregistrées"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useImperativeHandle(ref, () => ({ save }));

  // Enregistrement automatique (création / édition / suppression / déplacement) — met aussi
  // à jour le badge « x repères » en temps réel via onSaved, sans toast ni repli visuel.
  const autosave = async () => {
    const clean = Object.fromEntries(Object.entries(data).map(([k, v]) => [k, (v || []).filter((n) => (n.note || "").trim())]));
    try { await api.put(`/soins/${soinId}`, { anatomie: clean }); onSaved?.(clean); } catch (_) { /* silencieux */ }
  };
  const firstRef = useRef(true);
  useEffect(() => {
    if (firstRef.current) { firstRef.current = false; return; }
    const t = setTimeout(autosave, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const boardProps = { colors, open, setOpen: selectOpen, onAdd: addNote, onEdit: editNote, onDel: delNote, onClose: closeNote };
  return (
    <div className="space-y-4" data-testid="fs-anatomie-panel">
      <p className="text-sm text-slate-500">Cliquez sur une zone pour ajouter une note. Glissez un repère pour le déplacer, survolez-le pour l'agrandir.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Board title="Face" region="front" src={IMAGES.front} notes={data.front} {...boardProps} />
        <Board title="Dos" region="back" src={IMAGES.back} notes={data.back} {...boardProps} />
      </div>
      <div className="max-w-md mx-auto">
        <Board title="Système digestif" region="digestive" src={IMAGES.digestive} notes={data.digestive} {...boardProps} />
      </div>
    </div>
  );
});

export default AnatomyTab;

function Board({ title, region, src, notes, colors, open, setOpen, onAdd, onEdit, onDel, onClose }) {
  const [hover, setHover] = useState(null);
  const boardRef = useRef(null);
  const dragRef = useRef(null);

  const click = (e) => {
    if (e.target.closest("[data-pin]")) return;
    const r = e.currentTarget.getBoundingClientRect();
    onAdd(region, ((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };
  const onPinDown = (e, n) => {
    e.stopPropagation(); e.preventDefault();
    dragRef.current = { id: n.id, moved: false };
    const move = (ev) => {
      const r = boardRef.current.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((ev.clientX - r.left) / r.width) * 100));
      const y = Math.min(100, Math.max(0, ((ev.clientY - r.top) / r.height) * 100));
      dragRef.current.moved = true;
      onEdit(region, n.id, { x, y });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      const d = dragRef.current; dragRef.current = null;
      if (d && !d.moved) setOpen(d.id);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <div className="bg-white sx-card overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-100 text-xs font-bold uppercase tracking-wider text-slate-400">{title}</div>
      <div ref={boardRef} className="relative select-none cursor-crosshair" onClick={click} data-testid={`anat-board-${region}`}>
        <img src={src} alt={title} className="w-full block" draggable={false} />
        {(notes || []).map((n) => {
          const isOpen = open === n.id, isHover = hover === n.id;
          return (
            <div key={n.id} data-pin data-testid={`anat-pin-${n.id}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 ${isHover || isOpen ? "z-20" : "z-10"}`}
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
              onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}>
              {!isOpen ? (
                <div onMouseDown={(e) => onPinDown(e, n)}
                  className={`cursor-grab active:cursor-grabbing flex items-start gap-1 px-2 py-1 rounded-lg shadow border border-black/5 text-[11px] font-medium text-slate-800 origin-center transition-transform ${isHover ? "scale-110 max-w-[180px]" : "max-w-[140px]"}`}
                  style={{ background: n.color }}>
                  <span className="w-2 h-2 mt-1 rounded-full bg-black/30 shrink-0" />
                  <span className={isHover ? "line-clamp-2 whitespace-pre-wrap break-words" : "truncate"}>{isHover ? (n.note || "Note") : short(n.note)}</span>
                </div>
              ) : (
                <div data-note className="w-56 rounded-lg shadow-xl border border-black/10 p-2.5 text-slate-800" style={{ background: n.color }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex gap-1">
                      {colors.map((c) => <button key={c} onMouseDown={(e) => e.preventDefault()} onClick={() => onEdit(region, n.id, { color: c })} className={`w-4 h-4 rounded-full border ${n.color === c ? "ring-2 ring-slate-500" : "border-black/10"}`} style={{ background: c }} />)}
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`anat-del-${n.id}`} onClick={() => onDel(region, n.id)} className="text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => onClose(region, n.id)} className="text-slate-500 hover:text-slate-800"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <textarea autoFocus data-testid={`anat-note-${n.id}`} value={n.note} onChange={(e) => onEdit(region, n.id, { note: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onClose(region, n.id); } }}
                    onBlur={(e) => { const box = e.currentTarget.closest("[data-note]"); if (box && box.contains(e.relatedTarget)) return; onClose(region, n.id); }}
                    rows={3} placeholder="Votre note…" className="w-full text-xs bg-white/70 rounded p-1.5 outline-none resize-none" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
